/** @jsxImportSource @emotion/react */
import { css, useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import DropDownSelect from '@DropDownSelect';
import { useEffect, useReducer, useState, memo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import getExternalSelectDependencies from '../../Helpers/externalSelect';
import { getErrorMessage } from '../../Helpers/validation';
import { getExternalSelectOptionsAction } from '../../Lib/redux/sagas/GetExternalSelectOptions/getExternalSelectOptions';
import {
  clearSelectedOptionsAndValue,
  setComponentValidationStatus,
  setSelectedOptions,
  updateValue,
} from '../../Lib/redux/slices/components';
import QuestionText from '../QuestionText/QuestionText';
import {
  getDependentFieldValuesAndRequestField,
  getRemainingSelectComponentIds,
  isLastSelectOnPage,
  isNextSelectToBeAnswered,
} from './ExternalSelect.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const removeDefaultOption = (options) => {
  let defaultOptionValue;
  const optionsWithoutDefault = options.filter((option) => {
    if (option.id === 'default_select_text') {
      defaultOptionValue = option.value;
      return false;
    }
    return true;
  });

  return [defaultOptionValue, optionsWithoutDefault];
};

const getSelectedOptionId = (value, options) => {
  const selectedOption = options.find((option) => option.value === value);
  return selectedOption.id;
};

const ExternalSelectComponent = ({
  autoAdvance,
  componentId,
  iFrameId,
  subheading,
  formId,
  isHydrationDisabled = false, // only needed for unit tests
}) => {
  const dispatch = useDispatch();
  const [selectionCount, triggerSelection] = useReducer((x) => x + 1, 0);
  const theme = useTheme();
  const countryCode = useSelector((store) => store.formConfig.countryCode);
  const pages = useSelector((store) => store.paging.pages);
  const components = useSelector((store) => store.components);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const { validation, input, component: settings } = component;
  const { value, options } = input;
  const {
    text,
    displayQuestionLabelAsPlaceholder,
    mappedQuestionId: currentAdmiralQuestionId,
  } = settings;
  const environmentExternalSelectDependencies = getExternalSelectDependencies(
    components,
    currentAdmiralQuestionId
  );

  const dropDownSelectExperimentName = useGetExperimentName({
    componentId,
    componentName: 'DropDownSelect',
  });

  const dependentFieldValues = getDependentFieldValuesAndRequestField({
    admiralQuestionId: currentAdmiralQuestionId,
    components,
    pages,
    environmentExternalSelectDependencies,
  });

  const [dependentFieldPreviousValues, setDependentFieldPreviousValues] =
    useState(undefined);
  const [previousOptions, setPreviousOptions] = useState(undefined);
  const [hydrated, setHydrated] = useState(false);

  const errorMessage = getErrorMessage(validation.constraints);

  const [defaultOptionValue, optionsWithoutDefault] =
    removeDefaultOption(options);

  const handleSelect = (params) => {
    const { value: newValue } = params;
    const selectedOptionId = getSelectedOptionId(newValue, options);
    batch(() => {
      getRemainingSelectComponentIds({
        componentId,
        components,
        pages,
        environmentExternalSelectDependencies,
      }).forEach((nextSelectComponentId) => {
        dispatch(
          clearSelectedOptionsAndValue({
            componentId: nextSelectComponentId,
          })
        );
      });

      dispatch(
        updateValue({
          componentId,
          value: newValue,
        })
      );

      dispatch(
        setSelectedOptions({
          componentId,
          selectedOptions: [selectedOptionId],
        })
      );

      dispatch(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );

      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));

      triggerSelection();
    });
  };

  useEffect(() => {
    if (!options) {
      return;
    }

    setHydrated(true);
    if (JSON.stringify(previousOptions) !== JSON.stringify(options)) {
      setPreviousOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (!isNextSelectToBeAnswered(componentId, components, pages)) {
      return;
    }

    if (
      JSON.stringify(dependentFieldValues) !==
      JSON.stringify(dependentFieldPreviousValues)
    ) {
      setHydrated(false);
      setDependentFieldPreviousValues(dependentFieldValues);
      dispatch(
        getExternalSelectOptionsAction(
          componentId,
          environmentExternalSelectDependencies[currentAdmiralQuestionId].type,
          dependentFieldValues
        )
      );
    }
  }, [dependentFieldValues]);

  useEffect(() => {
    if (
      selectionCount > 0 &&
      autoAdvance &&
      isLastSelectOnPage(componentId, components, pages)
    ) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectionCount]);

  const handleBlur = () => {
    batch(() => {
      dispatch(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const loadingDotsAnimation = (
    <div className="dropDownLoadingDots" data-testid="dropDownHydration">
      <span />
      <span />
      <span />
    </div>
  );
  let questionLabelOrLoadingDotsAnimation;
  if (isHydrationDisabled || hydrated) {
    questionLabelOrLoadingDotsAnimation = displayQuestionLabelAsPlaceholder
      ? text
      : defaultOptionValue;
  } else {
    questionLabelOrLoadingDotsAnimation = loadingDotsAnimation;
  }

  return (
    <>
      {!displayQuestionLabelAsPlaceholder && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
          subheading={renderMustache({
            template: subheading,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <Box
        css={css({
          marginTop: theme.mvf.spacing.medium,
          marginBottom: theme.mvf.spacing.small,
        })}
        data-testid={`externalSelectContainer-${currentAdmiralQuestionId}`}
      >
        <DropDownSelect
          errorMessage={errorMessage}
          id={`${componentId}`}
          onChange={handleSelect}
          label={questionLabelOrLoadingDotsAnimation}
          fullWidth
          onBlur={() => handleBlur()}
          value={value || ''}
          disabled={
            (!hydrated && !isHydrationDisabled) ||
            !isNextSelectToBeAnswered(componentId, components, pages)
          }
          experimentName={dropDownSelectExperimentName}
          dataTestId="dropDownSelect"
        >
          {optionsWithoutDefault.map((option) => {
            return (
              <MenuItem key={`${option.value}`} value={option.value}>
                {option.value}
              </MenuItem>
            );
          })}
        </DropDownSelect>
      </Box>
    </>
  );
};

const ExternalSelect = memo(ExternalSelectComponent);

export default ExternalSelect;
